import React, { useState, useEffect } from 'react';
import { TextField, Button, Container, Typography, Box, IconButton, Snackbar, Alert } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import walletApi from '../../api/wallet';
import AllocationEditor from './AllocationEditor';

const ProfileEdit = () => {
    const [userData, setUserData] = useState({
        Name: '',
        Email: '',
        Distribution: {
            Percentages: {}
        },
        Allocation: {
            Percentages: {},
            ByInstrument: {}
        }
    });
    const [editingDistributionKey, setEditingDistributionKey] = useState(null);
    const [newDistributionKeyName, setNewDistributionKeyName] = useState('');
    const [error, setError] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await walletApi.get('/user')
                const user = response.data;
                if (user.Distribution.Percentages === null) {
                    user.Distribution.Percentages = {};
                }
                if (user.Allocation.Percentages === null) {
                    user.Allocation.Percentages = {};
                }
                if (user.Allocation.ByInstrument === null || user.Allocation.ByInstrument === undefined) {
                    user.Allocation.ByInstrument = {};
                }
                setUserData(user);
            } catch (error) {
                showError('Falha ao carregar dados do usuario.');
            }
        };

        fetchUserData();
    }, []);

    const showError = (message) => {
        setError(message);
        setSnackbarOpen(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUserData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleDistributionChange = (e, key) => {
        let { value } = e.target;
        if (value === '') value = 0;
        const percentage = Number(value);
        setUserData((prevState) => ({
            ...prevState,
            Distribution: {
                ...prevState.Distribution,
                Percentages: {
                    ...prevState.Distribution.Percentages,
                    [key]: percentage,
                },
            },
        }));
    };

    const handleDistributionFocus = (key) => {
        setUserData((prevState) => ({
            ...prevState,
            Distribution: {
                ...prevState.Distribution,
                Percentages: {
                    ...prevState.Distribution.Percentages,
                    [key]: prevState.Distribution.Percentages[key] === 0 ? '' : prevState.Distribution.Percentages[key],
                },
            },
        }));
    };

    const handleDistributionKeyEdit = (e) => {
        setNewDistributionKeyName(e.target.value);
    };

    const handleDistributionKeyEditBlur = (oldKey) => {
        if (newDistributionKeyName && newDistributionKeyName !== oldKey) {
            setUserData((prevState) => {
                const updatedPercentages = {};
                const existingPercentages = prevState.Distribution.Percentages;
                if (existingPercentages[newDistributionKeyName] !== undefined) {
                    showError('Nome da categoria ja existe.');
                    return prevState;
                } else {
                    Object.keys(existingPercentages).forEach((k) => {
                        if (k === oldKey) {
                            updatedPercentages[newDistributionKeyName] = existingPercentages[oldKey];
                        } else {
                            updatedPercentages[k] = existingPercentages[k];
                        }
                    });
                    return {
                        ...prevState,
                        Distribution: {
                            ...prevState.Distribution,
                            Percentages: updatedPercentages,
                        },
                    };
                }
            });
        }
        setEditingDistributionKey(null);
        setNewDistributionKeyName('');
    };

    const handleAddDistribution = () => {
        const newKey = `Nova Categoria ${Object.keys(userData.Distribution.Percentages).length + 1}`;
        setUserData((prevState) => ({
            ...prevState,
            Distribution: {
                ...prevState.Distribution,
                Percentages: {
                    ...prevState.Distribution.Percentages,
                    [newKey]: 0,
                },
            },
        }));
    };

    const handleRemoveDistribution = (key) => {
        setUserData((prevState) => {
            const { [key]: value, ...rest } = prevState.Distribution.Percentages;
            return {
                ...prevState,
                Distribution: {
                    ...prevState.Distribution,
                    Percentages: rest,
                },
            };
        });
    };

    const handleAllocationChange = (newAllocation) => {
        setUserData((prevState) => ({
            ...prevState,
            Allocation: newAllocation,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const distributionPercentages = userData.Distribution.Percentages || {};
            const hasDistribution = Object.keys(distributionPercentages).length > 0;
            if (hasDistribution) {
                const distributionTotal = Object.values(distributionPercentages).reduce(
                    (sum, value) => sum + Number(value || 0),
                    0
                );
                if (distributionTotal !== 100) {
                    showError('A soma da distribuicao deve ser 100%.');
                    return;
                }
            }

            const allocationTotal = Object.values(userData.Allocation.Percentages || {}).reduce(
                (sum, value) => sum + Number(value || 0),
                0
            );
            if (allocationTotal > 100) {
                showError('A soma da alocacao nao pode ser maior que 100%.');
                return;
            }

            const payload = {
                ...userData,
                Distribution: hasDistribution ? userData.Distribution : { Percentages: null },
                Allocation: Object.keys(userData.Allocation.Percentages || {}).length > 0
                    ? userData.Allocation
                    : { Percentages: null, ByInstrument: null },
            };

            const response = await walletApi.put('/user', payload)
            if (response.data.IsValid) {
                alert('Perfil atualizado com sucesso!');
                setError('');
                setSnackbarOpen(false);
            } else {
                showError(`Erro: ${response.data.Errors.join(', ')}`);
            }
        } catch (error) {
            let errorMessage = 'Ocorreu um erro ao atualizar o perfil.';
            if (error.response && error.response.data && error.response.data.Errors) {
                errorMessage = `Erro: ${error.response.data.Errors.join(', ')}`;
            } else if (error instanceof Error && error.message) {
                errorMessage = error.message;
            }
            showError(errorMessage);
        }
    };

    return (
        <Container>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 4 }}>
                <Typography variant="h4" component="h1">
                    Editar Usuario
                </Typography>
                <TextField
                    label="Nome"
                    name="Name"
                    value={userData.Name}
                    onChange={handleInputChange}
                    fullWidth
                />
                <TextField
                    label="Email"
                    name="Email"
                    value={userData.Email}
                    onChange={handleInputChange}
                    fullWidth
                />

                <Typography variant="h6" component="h2" sx={{ mt: 2 }}>
                    Distribuicao de Ganhos
                </Typography>
                {Object.keys(userData.Distribution.Percentages).map((key) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {editingDistributionKey === key ? (
                            <TextField
                                label="Categoria"
                                value={newDistributionKeyName}
                                onChange={handleDistributionKeyEdit}
                                onBlur={() => handleDistributionKeyEditBlur(key)}
                                autoFocus
                                fullWidth
                            />
                        ) : (
                            <TextField
                                label="Categoria"
                                value={key}
                                onFocus={() => {
                                    setEditingDistributionKey(key);
                                    setNewDistributionKeyName(key);
                                }}
                                fullWidth
                                inputProps={{ readOnly: true }}
                            />
                        )}
                        <TextField
                            label="% Alocada"
                            value={userData.Distribution.Percentages[key]}
                            onChange={(e) => handleDistributionChange(e, key)}
                            onFocus={() => handleDistributionFocus(key)}
                            type="number"
                            fullWidth
                        />
                        <IconButton onClick={() => handleRemoveDistribution(key)}>
                            <RemoveCircleIcon />
                        </IconButton>
                    </Box>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton onClick={handleAddDistribution}>
                        <AddCircleIcon />
                    </IconButton>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <AllocationEditor
                        allocation={userData.Allocation}
                        onChange={handleAllocationChange}
                        onError={showError}
                    />
                </Box>

                <Button variant="contained" color="primary" onClick={handleSubmit} sx={{ mt: 2 }}>
                    Salvar
                </Button>
            </Box>
        </Container>
    );
};

export default ProfileEdit;
